import React, { useContext } from 'react';
// eslint-disable-next-line no-duplicate-imports
import type { ReactNode } from 'react';
import cx from 'classnames';

import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import ClusterLayout from '../ClusterLayout';
import Text from '../Text';
import { ModalContext } from './ModalContext';

import stylesheet from './Modal.css';

export type ModalFooterProps = {
  /** The footer content inside the modal.  */
  children?: React.ReactNode;
  /** Change the content justification for the footer. To ensure design consistency, Only a subset (flex-end and `space-between`)
   * of normal justification choices are available.
   */
  justifyContent?: 'flex-end' | 'space-between';
  /** Additional class names to apply to the footer. */
  className?: string;
  /**
   * Provides a space for supplemental information like legalese or links to optional terms of service.
   * Only available in Medium and Large modal sizes. Should never contain information that is vital
   * for users to read.',
   */
  note?: ReactNode;
};

const ModalFooter = ({
  children,
  className,
  justifyContent = 'flex-end',
  note,
  ...rest
}: ModalFooterProps) => {
  const isRedesigned = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );
  const isBugFixG = mcdsFlagCheck('xp_mcds_redesign_bug_fixes_G');
  const { size } = useContext(ModalContext);

  // Only show note for medium and large modals
  const isNotSmall = size !== 'small';
  return (
    <div
      className={cx(
        stylesheet.footer,
        {
          [stylesheet.footerWithNote]: note,
        },
        className,
      )}
      {...rest}
    >
      {isRedesigned ? (
        isBugFixG ? (
          <>
            {isNotSmall &&
              (typeof note === 'string' ? (
                <Text appearance="small-tertiary" className={stylesheet.note}>
                  {note}
                </Text>
              ) : (
                <>
                  {note && (
                    <span className={stylesheet.footerNote}>{note}</span>
                  )}
                </>
              ))}

            <div className={stylesheet.footerContent}>{children}</div>
          </>
        ) : (
          <>
            {note && <span className={stylesheet.footerNote}>{note}</span>}
            <div className={stylesheet.footerContent}>{children}</div>
          </>
        )
      ) : (
        <ClusterLayout justifyContent={justifyContent}>
          {children}
        </ClusterLayout>
      )}
    </div>
  );
};

export default ModalFooter;
